// components/island/useAnrufKontext.ts
// Lädt Objekt/Einheit und offene Vorgänge zum erkannten Anrufer nach, damit die
// Anrufkarte zeigt, worum es vermutlich geht — nicht nur wer anruft. Schlanker
// Hook nach dem Vorbild von useVerpassteAnrufe.ts: lädt einmal je erkanntem
// Anrufer, kein Realtime-Abo nötig (die Karte lebt nur für die Dauer des
// Gesprächs). Die Verdichtung (was zählt als "offen", wie viele passen) steckt
// in lib/telefonie/anrufKontext.ts — hier wird nur nachgeladen.
import { useEffect, useState } from 'react';
import { supabase, isDemoMode } from '../../lib/supabase/client';
import {
  zuKontext, type AnrufKontext, type KontextObjekt, type KontextTicket,
} from '../../lib/telefonie/anrufKontext';
import type { AnrufKarte } from '../../lib/telefonie/anrufKarte';

const OBJEKT_FELDER = 'id, bezeichnung, strasse, hausnummer, plz, stadt';
// faellig_am bewusst nicht selektiert — KontextTicket führt das Feld nicht
// (siehe Kommentar in anrufKontext.ts), die Anrufkarte sortiert nicht danach.
const TICKET_FELDER = 'id, ticket_nummer, titel, status, prioritaet';

export function useAnrufKontext(karte: AnrufKarte | null): AnrufKontext | null {
  const [kontext, setKontext] = useState<AnrufKontext | null>(null);

  // Primitive Werte statt der Karte selbst als Abhängigkeit: eine neue Karten-
  // Referenz mit denselben Werten (z.B. nur der Status ändert sich) soll nicht
  // erneut nachladen.
  const zielTyp = karte?.ziel?.typ ?? null;
  const zielId = karte?.ziel?.id ?? null;
  const objektId = karte?.objekt_id ?? null;

  useEffect(() => {
    // Immer zuerst zurücksetzen: ruft direkt nach Anrufer A ein Anrufer B an
    // (beide erkannt), darf B's Karte nicht kurz noch A's Zusammenhang zeigen,
    // während die neue Abfrage läuft.
    setKontext(null);

    if (isDemoMode() || !zielTyp || !zielId) return;
    let abgebrochen = false;

    async function laden() {
      let objekt: KontextObjekt | null = null;
      let einheit: string | null = null;
      let tickets: KontextTicket[] = [];
      let fehler = false;
      const aufgaben: PromiseLike<void>[] = [];

      if (objektId) {
        aufgaben.push(
          supabase.from('objekte').select(OBJEKT_FELDER).eq('id', objektId).maybeSingle()
            .then(({ data, error }) => {
              if (error) { console.error('Anrufkontext (Objekt):', error.message); fehler = true; }
              else objekt = data as KontextObjekt | null;
            }),
        );
      }

      // Vorgänge NUR bei Mietern, über mieter_id — das ist die einzige
      // eindeutige Zuordnung. Ein objekt_id-Zweig für andere Rollen (Eigentümer/
      // Dienstleister/Kontakt) wäre nicht nur totes Feld (v_telefon_kontakte
      // liefert objekt_id ausschließlich für Mieter — "case … when 'mieter'
      // then m.objekt_id else null end"; live 0 von 246 Eigentümern, 0 von 280
      // Dienstleistern, 0 von 6 Kontakten haben es gesetzt), sondern auch in
      // der Sache falsch: ein Eigentümer hat oft mehrere Objekte, ein
      // Dienstleister Dutzende Aufträge — ein einzelnes Objekt ordnet dessen
      // Vorgänge nicht eindeutig zu. Würde die Sicht objekt_id für diese
      // Rollen später doch befüllen, zeigte die Karte einem anrufenden
      // Handwerker unbeabsichtigt fremde Mieter-Vorgänge des ganzen Hauses.
      if (zielTyp === 'mieter') {
        aufgaben.push(
          supabase.from('mieter').select('einheit').eq('id', zielId).maybeSingle()
            .then(({ data, error }) => {
              if (error) { console.error('Anrufkontext (Einheit):', error.message); fehler = true; }
              else einheit = (data as { einheit: string | null } | null)?.einheit ?? null;
            }),
        );
        aufgaben.push(
          supabase.from('tickets').select(TICKET_FELDER).eq('mieter_id', zielId).is('geloescht_am', null)
            .then(({ data, error }) => {
              if (error) { console.error('Anrufkontext (Tickets):', error.message); fehler = true; }
              else tickets = (data as KontextTicket[] | null) ?? [];
            }),
        );
      }

      await Promise.all(aufgaben);
      // Kein Status-Filter hier — "offen" hat genau eine Definition, in zuKontext.
      if (!abgebrochen) setKontext({ ...zuKontext(objekt, tickets, einheit), fehler });
    }
    void laden();

    return () => {
      abgebrochen = true;
    };
  }, [zielTyp, zielId, objektId]);

  return kontext;
}
